import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import {
  HouseAndLandConfigurationInterface,
  MenuItemInterface,
  PriceRange,
  PropertyConfigurationInterface,
  RootStateTypeExtra,
} from '@store/types'

import {
  EnvisionVRConfigurationInterface,
  GallerySettingsInterface,
  LogoInterface,
  MarkerColourInterface,
  SnaploaderConfigurationInterface,
  StatusColors,
  StatusLabels,
  ThemeConfigInterface,
  VideoScreensaver,
} from '@api/config'
import { Templates } from '@api/showcase-room'

import { ProjectType } from '@utilities/adgroup-utilities/constants/common'

export interface ConfigInterface {
  logo: LogoInterface
  prices: PriceRange
  markerColour: MarkerColourInterface
  threeDViewerUrl: string
  propertyConfig: PropertyConfigurationInterface
  houseAndLandConfig: HouseAndLandConfigurationInterface
  aspects: Array<string>
  unitTypes: Array<string>
  longhand: boolean
  showPrice: boolean
  showcase3DIntegration: boolean
  hideSoldPrice: boolean
  showcaseRefreshTriggerKey: string
  hideFilter: boolean
  sidePanelTitle: string
  hidePropertyInfoName: boolean
  hideInfoPanelAspect: boolean
  propertyPrefix: string
  propertyGalleryControlStyle: string
  onlyShowPropertyGalleries: boolean
  propertyGalleryTab: {
    active: boolean
    duration: number
  }
  snaploaderConfiguration: SnaploaderConfigurationInterface
  envisionVRConfiguration: EnvisionVRConfigurationInterface
  theme: ThemeConfigInterface
  gallerySettings: GallerySettingsInterface
  navigationSettings: Array<MenuItemInterface>
  disableSoldProperty: boolean
  type: string
  lightUpBuildingModelOnFilter: boolean
  statusLabels: StatusLabels
  statusColors: StatusColors
  templates: Templates
  videoScreensaver: VideoScreensaver
  configLoaded?: boolean
}

export interface ConfigInterfaceOptional {
  logo?: LogoInterface
  prices?: PriceRange
  markerColour?: MarkerColourInterface
  threeDViewerUrl?: string
  propertyConfig?: PropertyConfigurationInterface
  houseAndLandConfig?: HouseAndLandConfigurationInterface
  aspects?: Array<string>
  unitTypes?: Array<string>
  longhand?: boolean
  showPrice?: boolean
  showcase3DIntegration?: boolean
  hideSoldPrice?: boolean
  showcaseRefreshTriggerKey?: string
  hideFilter?: boolean
  sidePanelTitle?: string
  hidePropertyInfoName?: boolean
  hideInfoPanelAspect?: boolean
  propertyPrefix?: string
  propertyGalleryControlStyle?: string
  onlyShowPropertyGalleries?: boolean
  propertyGalleryTab?: {
    active: boolean
    duration: number
  }
  snaploaderConfiguration?: SnaploaderConfigurationInterface
  envisionVRConfiguration?: EnvisionVRConfigurationInterface
  theme?: ThemeConfigInterface
  lightUpBuildingModelOnFilter?: boolean
  statusLabels?: StatusLabels
  statusColors?: StatusColors
  templates?: Templates
  videoScreensaver?: VideoScreensaver
}

const configDefault: ConfigInterface = {
  hideSoldPrice: false,
  longhand: false,
  showPrice: false,
  showcase3DIntegration: false,
  logo: {
    visible: false,
    url: '',
  },
  prices: {
    max: [],
    min: [],
  },
  markerColour: {
    available: '#FFFFFF',
    sold: '#D14417',
    area: '#FFFFFF',
    levels: '#FFFFFF',
  },
  threeDViewerUrl: '',
  propertyConfig: {},
  houseAndLandConfig: {},
  aspects: [],
  unitTypes: [],
  showcaseRefreshTriggerKey: '',
  hideFilter: false,
  sidePanelTitle: 'Unit',
  hidePropertyInfoName: false,
  hideInfoPanelAspect: false,
  propertyPrefix: 'Unit',
  propertyGalleryControlStyle: 'thumbnail',
  onlyShowPropertyGalleries: false,
  propertyGalleryTab: {
    active: false,
    duration: 0,
  },
  snaploaderConfiguration: {
    active: false,
    sceneSnapId: '',
    sceneModelId: '',
  },
  envisionVRConfiguration: {
    active: false,
    url: '',
    zoom: 0.5,
    rotateCameraBy: 0,
  },
  theme: {
    font: '',
    mainColour: '',
    secondaryColour: '',
    tertiaryColour: '',
    neutralColour: '',
  },
  gallerySettings: {
    type: 'slide',
    intervalInSeconds: 7,
  },
  navigationSettings: [],
  disableSoldProperty: false,
  type: ProjectType.Apartment,
  lightUpBuildingModelOnFilter: false,
  statusLabels: {},
  statusColors: {},
  templates: {},
  videoScreensaver: {
    delay: 0,
    videoUrl: '',
    enabled: false,
  },
  configLoaded: false,
}

let initialState = configDefault

const projectConfig = localStorage.getItem('projectConfig')

if (projectConfig) {
  initialState = JSON.parse(projectConfig)
}

const projectConfigSlice = createSlice({
  name: 'projectConfig',
  initialState,
  reducers: {
    setProjectConfig: (
      state: ConfigInterface,
      action: PayloadAction<ConfigInterfaceOptional>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, ...action.payload })
      )
      return { ...state, ...action.payload }
    },
    setPrices: (state: ConfigInterface, action: PayloadAction<PriceRange>) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, prices: action.payload })
      )
      return { ...state, prices: action.payload }
    },
    setMarkerColour: (
      state: ConfigInterface,
      action: PayloadAction<MarkerColourInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, markerColour: action.payload })
      )
      return { ...state, markerColour: action.payload }
    },
    setThreeDViewerUrl: (
      state: ConfigInterface,
      action: PayloadAction<string>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, threeDViewerUrl: action.payload })
      )
      return { ...state, threeDViewerUrl: action.payload }
    },
    setPropertyConfig: (
      state: ConfigInterface,
      action: PayloadAction<PropertyConfigurationInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, propertyConfig: action.payload })
      )
      return { ...state, propertyConfig: action.payload }
    },
    setHouseAndLandConfig: (
      state: ConfigInterface,
      action: PayloadAction<HouseAndLandConfigurationInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, houseAndLandConfig: action.payload })
      )
      return { ...state, houseAndLandConfig: action.payload }
    },
    setAspect: (
      state: ConfigInterface,
      action: PayloadAction<Array<string>>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, aspects: action.payload })
      )
      return { ...state, aspects: action.payload }
    },
    setNavigationSettings: (
      state: ConfigInterface,
      action: PayloadAction<Array<MenuItemInterface>>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, navigationSettings: action.payload })
      )
      return { ...state, navigationSettings: action.payload }
    },
    setUnitTypes: (
      state: ConfigInterface,
      action: PayloadAction<Array<string>>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, unitTypes: action.payload })
      )
      return { ...state, unitTypes: action.payload }
    },
    setShowcaseRefreshTriggerKey: (
      state: ConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        showcaseRefreshTriggerKey: action.payload,
      }
      localStorage.setItem('projectConfig', JSON.stringify(newState))
      return newState
    },
    setSnaploaderConfiguration: (
      state: ConfigInterface,
      action: PayloadAction<SnaploaderConfigurationInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, snaploaderConfiguration: action.payload })
      )
      return { ...state, snaploaderConfiguration: action.payload }
    },
    setEnvisionVRConfiguration: (
      state: ConfigInterface,
      action: PayloadAction<EnvisionVRConfigurationInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, envisionVRConfiguration: action.payload })
      )
      return { ...state, envisionVRConfiguration: action.payload }
    },
    setThemeConfiguration: (
      state: ConfigInterface,
      action: PayloadAction<ThemeConfigInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, theme: action.payload })
      )
      return { ...state, theme: action.payload }
    },
    setVideoScreensaver: (
      state: ConfigInterface,
      action: PayloadAction<VideoScreensaver>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, videoScreensaver: action.payload })
      )
      return { ...state, videoScreensaver: action.payload }
    },
    setGallerySettings: (
      state: ConfigInterface,
      action: PayloadAction<GallerySettingsInterface>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, gallerySettings: action.payload })
      )
      return { ...state, gallerySettings: action.payload }
    },
    setDisableSoldPropertyInfo: (
      state: ConfigInterface,
      action: PayloadAction<boolean>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, disableSoldProperty: action.payload })
      )
      return { ...state, disableSoldProperty: action.payload }
    },
    setProjectType: (
      state: ConfigInterface,
      action: PayloadAction<ProjectType>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({ ...state, type: action.payload })
      )
      return { ...state, type: action.payload }
    },
    setLightUpBuildingModelOnFilter: (
      state: ConfigInterface,
      action: PayloadAction<boolean>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({
          ...state,
          lightUpBuildingModelOnFilter: action.payload,
        })
      )
      return { ...state, lightUpBuildingModelOnFilter: action.payload }
    },
    setStatusLabels: (
      state: ConfigInterface,
      action: PayloadAction<StatusLabels>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({
          ...state,
          stat: action.payload,
        })
      )
      return { ...state, statusLabels: action.payload }
    },
    setStatusColors: (
      state: ConfigInterface,
      action: PayloadAction<StatusColors>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({
          ...state,
          stat: action.payload,
        })
      )
      return { ...state, statusColors: action.payload }
    },
    setTemplateConfigs: (
      state: ConfigInterface,
      action: PayloadAction<Templates>
    ) => {
      localStorage.setItem(
        'projectConfig',
        JSON.stringify({
          ...state,
          stat: action.payload,
        })
      )
      return { ...state, templates: action.payload }
    },
    removeProjectConfig: () => {
      localStorage.removeItem('projectConfig')
      return configDefault
    },
    setConfigLoaded: (
      state: ConfigInterface,
      action: PayloadAction<boolean>
    ) => ({ ...state, configLoaded: action.payload }),
  },
})

export const {
  setProjectConfig,
  removeProjectConfig,
  setThreeDViewerUrl,
  setMarkerColour,
  setPrices,
  setShowcaseRefreshTriggerKey,
  setSnaploaderConfiguration,
  setPropertyConfig,
  setHouseAndLandConfig,
  setAspect,
  setNavigationSettings,
  setUnitTypes,
  setEnvisionVRConfiguration,
  setThemeConfiguration,
  setVideoScreensaver,
  setGallerySettings,
  setDisableSoldPropertyInfo,
  setProjectType,
  setLightUpBuildingModelOnFilter,
  setStatusLabels,
  setStatusColors,
  setTemplateConfigs,
  setConfigLoaded,
} = projectConfigSlice.actions

export const selectProjectConfig = (state: RootStateTypeExtra) =>
  state.projectConfig

export const selectThemeConfig = (state: RootStateTypeExtra) =>
  state.projectConfig.theme

export default projectConfigSlice.reducer
